/**
 * IssueReportsPage — Browse and inspect in-app issue reports visible to the user.
 *
 * Shows a filterable list of issue reports. Clicking a row opens a detail
 * drawer with full evidence bundle, screenshot, correlation ID, and GitHub
 * issue link.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BugReportIcon from '@mui/icons-material/BugReport';
import axios from 'axios';

import issueReportService, {
  type IssueReportListItem,
  type IssueReportDetail,
} from '../../services/issueReportService';
import apiClient from '../../services/apiClient';
import { API_URL } from '../../config';
import { useAppSelector } from '../../hooks/reduxHooks';
import PageHeaderNavigation from '../../components/common/PageHeaderNavigation';

const SEVERITY_COLORS: Record<string, 'error' | 'warning' | 'default' | 'success'> = {
  critical: 'error',
  high: 'warning',
  medium: 'default',
  low: 'success',
};

const STATUS_COLORS: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
  open: 'error',
  investigating: 'warning',
  resolved: 'success',
  wont_fix: 'default',
};

interface ReplayEventEntry {
  timestamp: string;
  type: string;
  target: string;
  value?: string;
  scrollX?: number;
  scrollY?: number;
  key?: string;
}

const IssueReportsPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';

  const [reports, setReports] = useState<IssueReportListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Detail dialog
  const [detail, setDetail] = useState<IssueReportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [githubRetrying, setGithubRetrying] = useState(false);
  const [githubError, setGithubError] = useState<string | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await issueReportService.listIssueReports({
        category: filterCategory || undefined,
        severity: filterSeverity || undefined,
        status: filterStatus || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setReports(data.items);
      setTotalCount(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load issue reports');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterSeverity, filterStatus, page, rowsPerPage]);

  useEffect(() => {
    if (user) fetchReports();
  }, [user, fetchReports]);

  const detailRequestIdRef = useRef(0);

  const handleRowClick = async (reportId: number) => {
    const requestId = ++detailRequestIdRef.current;
    setDetailLoading(true);
    setDetailError(null);
    setGithubError(null);
    setDetail(null);
    if (screenshotUrl) { URL.revokeObjectURL(screenshotUrl); setScreenshotUrl(null); }
    try {
      const data = await issueReportService.getIssueReportDetail(reportId);
      if (requestId !== detailRequestIdRef.current) return;
      setDetail(data);
      if (data.has_screenshot) {
        try {
          const resp = await apiClient.get(`${API_URL}/issue-reports/${reportId}/screenshot`, {
            responseType: 'blob',
          });
          if (requestId !== detailRequestIdRef.current) return;
          setScreenshotUrl(URL.createObjectURL(resp.data));
        } catch {
          if (requestId === detailRequestIdRef.current) setScreenshotUrl(null);
        }
      }
    } catch (err) {
      if (requestId !== detailRequestIdRef.current) return;
      setDetailError(err instanceof Error ? err.message : 'Failed to load report detail');
    } finally {
      if (requestId === detailRequestIdRef.current) setDetailLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    };
  }, [screenshotUrl]);

  const handleGithubRetry = async () => {
    if (!detail || githubRetrying) return;
    const reportId = detail.id;
    const requestId = detailRequestIdRef.current;
    setGithubRetrying(true);
    setGithubError(null);
    try {
      const result = await issueReportService.retryGithubIssue(reportId);
      if (requestId === detailRequestIdRef.current) {
        setDetail((current) => current?.id === reportId ? { ...current, ...result } : current);
      }
      await fetchReports();
    } catch (err) {
      if (requestId === detailRequestIdRef.current) {
        setGithubError(axios.isAxiosError(err) && typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail : 'Could not create GitHub ticket. Check System Configuration → GitHub Issue Reporting.');
      }
    } finally {
      setGithubRetrying(false);
    }
  };

  const handleFilterChange = () => {
    setPage(0);
  };

  const replayEvents: ReplayEventEntry[] = detail?.evidence
    ? ((detail.evidence as Record<string, unknown>).replayEvents as ReplayEventEntry[]) ?? []
    : [];

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Sign in to view your issue reports.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeaderNavigation
        title={isAdmin ? 'Issue Reports' : 'My Issue Reports'}
        subtitle={isAdmin
          ? 'Browse and investigate in-app issue reports submitted by users in your organization'
          : 'View the status and details of issues you have reported'}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
        {/* Filters */}
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filterCategory}
              label="Category"
              onChange={(e) => { setFilterCategory(e.target.value); handleFilterChange(); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="transcription">Transcription</MenuItem>
              <MenuItem value="cpt_selection">CPT Selection</MenuItem>
              <MenuItem value="report_generation">Report Generation</MenuItem>
              <MenuItem value="intelligence_analytics">Intelligence / Analytics</MenuItem>
              <MenuItem value="ui_ux">UI / UX</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={filterSeverity}
              label="Severity"
              onChange={(e) => { setFilterSeverity(e.target.value); handleFilterChange(); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => { setFilterStatus(e.target.value); handleFilterChange(); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="investigating">Investigating</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="wont_fix">Won't Fix</MenuItem>
            </Select>
          </FormControl>

          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchReports}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : reports.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <BugReportIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No issue reports found.</Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Correlation ID</TableCell>
                    <TableCell>GitHub</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      onClick={() => handleRowClick(r.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>#{r.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.category.replace(/_/g, ' ')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.severity}
                          size="small"
                          color={SEVERITY_COLORS[r.severity] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          size="small"
                          color={STATUS_COLORS[r.status] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {r.correlation_id ? (
                          <Tooltip title={r.correlation_id}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {r.correlation_id.slice(0, 8)}...
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.github_issue_url ? (
                          <Link href={r.github_issue_url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                            #{r.github_issue_number} <OpenInNewIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(r.created_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={detail !== null || detailLoading}
        onClose={() => {
          if (screenshotUrl) { URL.revokeObjectURL(screenshotUrl); setScreenshotUrl(null); }
          setDetail(null); setDetailError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon color="warning" />
          Issue Report {detail ? `#${detail.id}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {detailError && (
            <Alert severity="error">{detailError}</Alert>
          )}
          {detail && (
            <Stack spacing={2}>
              {/* Status chips */}
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <Chip label={detail.category.replace(/_/g, ' ')} size="small" variant="outlined" />
                <Chip label={detail.severity} size="small" color={SEVERITY_COLORS[detail.severity] || 'default'} variant="outlined" />
                <Chip label={detail.status} size="small" color={STATUS_COLORS[detail.status] || 'default'} variant="outlined" />
              </Stack>

              {isAdmin && !detail.github_issue_url && (
                <Alert severity="warning" action={
                  <Button color="inherit" size="small" onClick={handleGithubRetry} disabled={githubRetrying}>
                    {githubRetrying ? 'Creating...' : 'Retry GitHub ticket'}
                  </Button>
                }>
                  {githubError || 'This report is saved, but no GitHub ticket is linked.'}
                </Alert>
              )}
              {/* Description */}
              <Box>
                <Typography variant="subtitle2">Description</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detail.description}</Typography>
                </Paper>
              </Box>

              {/* Metadata grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {detail.correlation_id && (
                  <Box>
                    <Typography variant="subtitle2">Correlation ID</Typography>
                    <TextField
                      value={detail.correlation_id}
                      size="small"
                      fullWidth
                      InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                    />
                  </Box>
                )}
                {detail.session_id && (
                  <Box>
                    <Typography variant="subtitle2">Session ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{detail.session_id}</Typography>
                  </Box>
                )}
                {detail.url && (
                  <Box>
                    <Typography variant="subtitle2">URL</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>{detail.url}</Typography>
                  </Box>
                )}
                {detail.user_agent && (
                  <Box>
                    <Typography variant="subtitle2">User Agent</Typography>
                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{detail.user_agent}</Typography>
                  </Box>
                )}
                {detail.github_issue_url && (
                  <Box>
                    <Typography variant="subtitle2">GitHub Issue</Typography>
                    <Link href={detail.github_issue_url} target="_blank" rel="noopener">
                      #{detail.github_issue_number} <OpenInNewIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                    </Link>
                  </Box>
                )}
              </Box>

              {/* Screenshot */}
              {detail.has_screenshot && (
                <Box>
                  <Typography variant="subtitle2">Screenshot</Typography>
                  <Box sx={{ mt: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    {screenshotUrl ? (
                      <img
                        src={screenshotUrl}
                        alt="Issue screenshot"
                        style={{ maxWidth: '100%', display: 'block' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        Loading screenshot...
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Session Replay Timeline */}
              {replayEvents.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">
                    Session Replay ({replayEvents.length} events)
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1, mt: 0.5, maxHeight: 250, overflow: 'auto' }}>
                    <Stack spacing={0.5}>
                      {replayEvents.map((evt, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.25,
                            borderBottom: i < replayEvents.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                          }}
                        >
                          <Chip
                            label={evt.type}
                            size="small"
                            variant="outlined"
                            color={
                              evt.type === 'click' ? 'primary' :
                              evt.type === 'input' ? 'secondary' :
                              evt.type === 'keydown' ? 'warning' :
                              'default'
                            }
                            sx={{ minWidth: 70, fontSize: '0.7rem' }}
                          />
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', flex: 1 }}>
                            {evt.target}
                          </Typography>
                          {evt.value && (
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              &quot;{evt.value}&quot;
                            </Typography>
                          )}
                          {evt.key && (
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              [{evt.key}]
                            </Typography>
                          )}
                          {evt.scrollX !== undefined && (
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                              ({evt.scrollX}, {evt.scrollY})
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                            {new Date(evt.timestamp).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Box>
              )}

              {/* Evidence bundle */}
              {detail.evidence && (
                <Box>
                  <Typography variant="subtitle2">Evidence Bundle</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, maxHeight: 300, overflow: 'auto' }}>
                    <pre style={{ fontSize: '0.7rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(detail.evidence, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              <Divider />

              <Stack direction="row" spacing={2}>
                <Box>
                  <Typography variant="subtitle2">Created</Typography>
                  <Typography variant="body2">{new Date(detail.created_at).toLocaleString()}</Typography>
                </Box>
                {detail.updated_at && (
                  <Box>
                    <Typography variant="subtitle2">Updated</Typography>
                    <Typography variant="body2">{new Date(detail.updated_at).toLocaleString()}</Typography>
                  </Box>
                )}
                {detail.resolved_at && (
                  <Box>
                    <Typography variant="subtitle2">Resolved</Typography>
                    <Typography variant="body2">{new Date(detail.resolved_at).toLocaleString()}</Typography>
                  </Box>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (screenshotUrl) { URL.revokeObjectURL(screenshotUrl); setScreenshotUrl(null); }
            setDetail(null); setDetailError(null);
          }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IssueReportsPage;
